import { useCallback, useEffect, useState } from 'react';
import { departmentsAPI, subjectsAPI } from '../services/api';
import { BookMarked, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const emptySubjectForm = {
  name: '',
  code: '',
  department_id: '',
  is_active: true,
};

const Subjects = () => {
  const { hasPermission, terminology } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptySubjectForm);

  const loadData = useCallback(async () => {
    const [subjectsRes, departmentsRes] = await Promise.all([
      subjectsAPI.getAll(),
      departmentsAPI.getAll(),
    ]);
    setSubjects(subjectsRes.data.subjects || []);
    setDepartments(departmentsRes.data.departments || []);
  }, []);

  useEffect(() => {
    loadData().catch(() => toast.error(`Failed to load ${terminology.areas.toLowerCase()}`));
  }, [loadData, terminology, terminology.areas]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      const payload = {
        ...form,
        department_id: form.department_id || null,
      };

      if (editingId) {
        await subjectsAPI.update(editingId, payload);
        toast.success(`${terminology.area} updated successfully`);
      } else {
        await subjectsAPI.create(payload);
        toast.success(`${terminology.area} created successfully`);
      }

      setForm(emptySubjectForm);
      setEditingId(null);
      setShowForm(false);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to save ${terminology.area.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (subject) => {
    setEditingId(subject.id);
    setForm({
      name: subject.name || '',
      code: subject.code || '',
      department_id: subject.department_id || '',
      is_active: subject.is_active,
    });
    setShowForm(true);
  };

  const handleDeactivate = async (subject) => {
    try {
      await subjectsAPI.update(subject.id, {
        name: subject.name,
        code: subject.code,
        department_id: subject.department_id,
        is_active: false,
      });
      toast.success('Work area deactivated successfully');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to deactivate work area');
    }
  };

  const canManage = hasPermission('subjects.manage');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{terminology.areas}</h1>
          <p className="text-gray-500 text-sm mt-1">Create {terminology.areas.toLowerCase()} and link them to departments.</p>
        </div>
        {canManage && (
          <button
            onClick={() => {
              setEditingId(null);
              setForm(emptySubjectForm);
              setShowForm((prev) => !prev);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            {showForm ? 'Close Form' : `Add ${terminology.area}`}
          </button>
        )}
      </div>

      {showForm && canManage && (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{terminology.area} Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Front Desk Operations"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{terminology.area} Code</label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="OPS101"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Department</label>
            <select
              value={form.department_id}
              onChange={(e) => setForm({ ...form, department_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select department</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>{department.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
            <select
              value={form.is_active ? 'active' : 'inactive'}
              onChange={(e) => setForm({ ...form, is_active: e.target.value === 'active' })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="md:col-span-2 flex gap-3">
            <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving...' : editingId ? `Update ${terminology.area}` : `Create ${terminology.area}`}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm(emptySubjectForm); }} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {subjects.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No work areas found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">{terminology.area}</th>
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">Department</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {subjects.map((subject) => (
                <tr key={subject.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <BookMarked size={16} className="text-blue-600" />
                      {subject.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{subject.code || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{subject.department_name || 'No department'}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${subject.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {subject.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {canManage ? (
                      <div className="flex gap-2">
                        <button onClick={() => startEdit(subject)} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50">
                          <Pencil size={12} />
                          Edit
                        </button>
                        {subject.is_active && (
                          <button onClick={() => handleDeactivate(subject)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">
                            <Trash2 size={12} />
                            Deactivate
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Subjects;
